import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import DatePicker from "@/components/DatePicker";
import { INR } from "@/lib/api";

/**
 * Mark-Paid / Add-Payment dialog for invoices. Fully controlled by parent.
 *
 * Props:
 *   open, onOpenChange — Dialog visibility.
 *   target — the invoice being paid (needs invoice_number, amount, entity_name,
 *            paid_amount, remaining_amount).
 *   form, setForm — controlled form state ({ utr_number, payment_id, payment_date, mode, paid_amount }).
 *   onSubmit — called when the Confirm button is clicked.
 */
export default function MarkPaidDialog({ open, onOpenChange, target, form, setForm, onSubmit }) {
  const remainingForFull = String(target?.remaining_amount || target?.amount || "");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Mark invoice paid</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-ink-2 mb-2">
          {target?.invoice_number} · {target && INR(target.amount)} · {target?.entity_name}
          {target?.paid_amount > 0 && (
            <div className="text-xs">
              Already paid {INR(target.paid_amount)} · Remaining {INR(target.remaining_amount)}
            </div>
          )}
        </div>
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={() => setForm({ ...form, mode: "full", paid_amount: remainingForFull })}
            className={`flex-1 h-9 rounded-sm text-sm ${
              form.mode === "full"
                ? "bg-[color:var(--aisensy-dark)] text-white"
                : "bg-[color:var(--bone)] text-ink"
            }`}
            data-testid="pay-mode-full"
          >
            Full payment
          </button>
          <button
            type="button"
            onClick={() => setForm({ ...form, mode: "partial" })}
            className={`flex-1 h-9 rounded-sm text-sm ${
              form.mode === "partial"
                ? "bg-[color:var(--aisensy-dark)] text-white"
                : "bg-[color:var(--bone)] text-ink"
            }`}
            data-testid="pay-mode-partial"
          >
            Partial payment
          </button>
        </div>
        <div className="space-y-3">
          {form.mode === "partial" && (
            <div>
              <Label className="eyebrow">
                Amount paid now (₹) <span className="text-[color:var(--overdue)]">*</span>
              </Label>
              <Input
                type="number"
                className="rounded-sm mt-1 tabular"
                value={form.paid_amount}
                max={target?.remaining_amount || target?.amount}
                onChange={(e) => setForm({ ...form, paid_amount: e.target.value })}
                data-testid="pay-partial-amount"
              />
            </div>
          )}
          <div>
            <Label className="eyebrow">UTR Number</Label>
            <Input
              className="rounded-sm mt-1 tabular"
              value={form.utr_number}
              onChange={(e) => setForm({ ...form, utr_number: e.target.value })}
              data-testid="pay-utr"
            />
          </div>
          <div className="text-center text-[10px] uppercase tracking-wider text-ink-2">or</div>
          <div>
            <Label className="eyebrow">Payment ID</Label>
            <Input
              className="rounded-sm mt-1 tabular"
              value={form.payment_id}
              onChange={(e) => setForm({ ...form, payment_id: e.target.value })}
              data-testid="pay-id"
            />
          </div>
          <div>
            <Label className="eyebrow">Payment Date</Label>
            <DatePicker
              testId="pay-date"
              value={form.payment_date}
              onChange={(v) => setForm({ ...form, payment_date: v })}
              placeholder="Select payment date"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            className="rounded-sm bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white"
            data-testid="pay-submit"
          >
            Confirm Paid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
